'use client';

/**
 * 会议议程独立查看页（v1.0）
 *
 * 路由：/meetingattendance/[meetingId]/agenda
 * 权限：登录用户即可访问（议程对参会人开放）；编辑按钮按 canManage 判定显隐
 *
 * 布局：返回按钮 + 会议元信息卡（标题/时间/操作按钮右上角）+ Excel 风议程表
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { toast } from 'sonner';
import { ApiClientError } from '@/lib/api-client';
import { ArrowLeft, Calendar, Edit, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UploadDialog } from '@/components/file-upload';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/auth';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { attachmentApi } from '@/services/api/attachment';
import { AgendaReadonlyView } from '@/features/meeting-attendance/agenda/AgendaReadonlyView';

interface MeetingLite {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string;
  creator?: { id: string };
}

function formatMmDdYyyy(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export default function AgendaViewPage() {
  useAuthGuard();
  const params = useParams();
  const meetingId = useMemo(() => {
    const raw = params?.meetingId;
    return Array.isArray(raw) ? raw[0] : raw || '';
  }, [params]);
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const ta = t.meetingAttendance.attachment;
  const { user, hasAnyRole, isAdmin } = useAuth();

  const [meeting, setMeeting] = useState<MeetingLite | null>(null);
  const [loading, setLoading] = useState(true);
  const [meetingUploadOpen, setMeetingUploadOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadMeeting = useCallback(async () => {
    if (!meetingId) return;
    try {
      setLoading(true);
      const data = await meetingAttendanceApi.getMeeting(meetingId);
      setMeeting(data as unknown as MeetingLite);
    } catch (err) {
      console.error('Failed to load meeting', err);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    loadMeeting();
  }, [loadMeeting]);

  const canManage = useMemo(() => {
    if (!user || !meeting) return false;
    if (isAdmin) return true;
    if (hasAnyRole(MEETING_MANAGEMENT_ROLES)) return true;
    if (meeting.creator?.id === user.id) return true;
    return false;
  }, [user, meeting, isAdmin, hasAnyRole]);

  const meetingUploader = useCallback(
    async (file: File, opts: { onProgress: (p: number) => void; signal: AbortSignal }) => {
      try {
        await attachmentApi.uploadMeetingAttachment(meetingId, file, {
          signal: opts.signal,
          onProgress: (e) => opts.onProgress(e.percent),
        });
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : undefined;
        toast.error(code === 'ATTACHMENT_QUOTA_EXCEEDED' ? ta.quotaExceeded : ta.uploadFailed);
        throw err;
      }
    },
    [meetingId, ta.quotaExceeded, ta.uploadFailed],
  );

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-4">
      <div className="mx-auto max-w-screen-xl space-y-3">
      {/* 返回按钮 */}
      <div>
        <Link href={`/meetingattendance/meetings/${meetingId}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {tag.viewPage.backToMeeting}
          </Button>
        </Link>
      </div>

      {/* 标题 + 会议元信息 + 操作按钮（右上角） */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900">{tag.viewPage.pageTitle}</h1>
            {loading ? (
              <div className="mt-2 text-sm text-gray-500">{t.meetingAttendance.common.loading}</div>
            ) : meeting ? (
              <div className="mt-2 space-y-1">
                <div className="text-base font-medium text-gray-800">{meeting.title}</div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {new Date(meeting.startTime).toLocaleString()} –{' '}
                    {new Date(meeting.endTime).toLocaleString()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-500">{tag.viewPage.meetingNotFound}</div>
            )}
          </div>

          {/* 右上角操作按钮 */}
          {canManage && (
            <div className="flex items-center gap-2">
              <Link href={`/meetingattendance/${meetingId}/agenda/edit`}>
                <Button variant="outline" size="sm">
                  <Edit className="mr-2 h-4 w-4" />
                  {tag.detailHeaderEditAgenda}
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={() => setMeetingUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                {tag.detailHeaderUploadMeetingFile}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 议程表格（含 Excel 风顶部 banner） */}
      {meetingId && (
        <AgendaReadonlyView
          meetingId={meetingId}
          canManage={canManage}
          reloadKey={reloadKey}
          bannerTitle={meeting ? `${formatMmDdYyyy(meeting.startTime)}  ${meeting.title}` : undefined}
        />
      )}

      {/* 会议级上传弹窗 */}
      <UploadDialog
        open={meetingUploadOpen}
        onOpenChange={(o) => {
          setMeetingUploadOpen(o);
          if (!o) setReloadKey((k) => k + 1);
        }}
        uploader={meetingUploader}
      />
      </div>
    </div>
  );
}
